import * as THREE from 'three'
import type { VisualParams } from '../../types'
import type { SharedUniforms } from '../AudioUniforms'
import { BasePrimitive } from './Primitive'
import { hsv2rgb, POINT_SPRITE_GLSL, ZONE_GLSL_COMMON, ZONE_GLSL_FRAG } from '../shaderChunks'

/**
 * Galaxy: a spiral disc of stars rotating around a bright core. The arms twist
 * with radius; the disc spins on the energy clock (faster with the music) and the
 * core swells/flares with the bass/beat. Warm core → cooler arms.
 */
export class Galaxy extends BasePrimitive {
  object3D: THREE.Points
  private material: THREE.ShaderMaterial

  constructor(uniforms: SharedUniforms) {
    super(uniforms)
    this.material = new THREE.ShaderMaterial({
      uniforms: uniforms as unknown as Record<string, THREE.IUniform>,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexShader: /* glsl */ `
        attribute float aRad;    // 0..1 distance from the core
        attribute float aAng;    // base arm angle
        attribute float aY;      // vertical offset (disc thickness)
        attribute float aSeed;
        uniform float uEnergyTime, uBass, uBeat, uBeatPulse, uReactivity, uBassPunch, uPixelRatio, uPointScale;
        varying float vRad;
        varying float vSeed;
        varying vec3 vDir;
        ${ZONE_GLSL_COMMON}
        void main() {
          float maxR = 5.0;
          float rad = aRad * maxR;
          float twist = 2.6;
          float ang = aAng + rad * twist * 0.32 + uEnergyTime * 0.3;
          float core = 1.0 - aRad;                       // 1 at center
          // When zone reaction is on, the core swell is frozen — only the zones react.
          float gate = 1.0 - uZoneEnabled;
          // Core swells with the bass + a clear beat punch (reliable BeatPulse).
          float bump = (uBass * uBassPunch * 1.6 + uBeatPulse * 1.6) * uReactivity * core * core * gate;
          // Zones ride the disc (longitude = world angle, latitude = radius) and
          // lift the stars they pass over out of the disc plane on their band's hit.
          vec3 zdir = zoneDirFromUV(vec2(fract(ang / 6.2831853), aRad));
          vDir = zdir;
          float zy = zoneDisp(zdir) * 1.2;
          vec3 pos = vec3(cos(ang) * rad, aY * (1.0 - aRad * 0.7) + bump * 0.7 + zy, sin(ang) * rad);
          vRad = aRad;
          vSeed = aSeed;
          vec4 mv = modelViewMatrix * vec4(pos, 1.0);
          gl_Position = projectionMatrix * mv;
          float px = (1.5 + core * 5.0 + uBeatPulse * 3.0 * uReactivity) * (0.6 + aSeed * 0.8);
          gl_PointSize = clamp(px * (6.0 / -mv.z), 1.0, 26.0) * uPixelRatio * uPointScale;
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform float uHue, uLevel, uBeat, uBeatPulse, uReactivity;
        varying float vRad;
        varying float vSeed;
        varying vec3 vDir;
        ${hsv2rgb}
        ${POINT_SPRITE_GLSL}
        ${ZONE_GLSL_COMMON}
        ${ZONE_GLSL_FRAG}
        void main() {
          float core = pointMask();
          if (core < 0.01) discard;
          // Warm core (yellow/white) fading to cool blue arms.
          float hue = fract(uHue + 0.08 + vRad * 0.5 + vSeed * 0.05);
          float sat = mix(0.2, 0.8, vRad);
          float val = (1.0 - vRad * 0.5) + uBeatPulse * 0.45 * uReactivity;
          vec3 col = hsv2rgb(vec3(hue, sat, val));
          float a = core * (uPointSolid > 0.5 ? 1.0 : (0.5 + (1.0 - vRad) * 0.5));
          // Zone reaction: stars under a zone take its color and glow.
          vec4 zc = zoneColor(vDir);
          vec3 outc = mix(col * core, zc.rgb * core, zc.a);
          gl_FragColor = vec4(outc, max(a, zc.a * core));
        }
      `,
    })
    this.object3D = new THREE.Points(new THREE.BufferGeometry(), this.material)
    this.object3D.frustumCulled = false
    this.rebuild({ density: 1 } as VisualParams)
  }

  update(params: VisualParams): void {
    const solid = params.pointSolid === true
    this.material.blending = solid ? THREE.NormalBlending : THREE.AdditiveBlending
    this.material.depthWrite = solid
  }

  rebuild(params: VisualParams): void {
    const count = Math.floor(40000 * (params.density ?? 1))
    const arms = 4
    const positions = new Float32Array(count * 3)
    const aRad = new Float32Array(count)
    const aAng = new Float32Array(count)
    const aY = new Float32Array(count)
    const aSeed = new Float32Array(count)
    for (let i = 0; i < count; i++) {
      const r = Math.pow(Math.random(), 0.6) // denser toward the core
      const arm = i % arms
      const ang = (arm / arms) * Math.PI * 2 + (Math.random() - 0.5) * 0.5
      aRad[i] = r
      aAng[i] = ang
      aY[i] = (Math.random() * 2 - 1) * 0.6
      aSeed[i] = Math.random()
    }
    const geo = this.object3D.geometry
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('aRad', new THREE.BufferAttribute(aRad, 1))
    geo.setAttribute('aAng', new THREE.BufferAttribute(aAng, 1))
    geo.setAttribute('aY', new THREE.BufferAttribute(aY, 1))
    geo.setAttribute('aSeed', new THREE.BufferAttribute(aSeed, 1))
    geo.computeBoundingSphere()
  }
}
